import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hidePaymentModal, showSpecialOffer, markDiscountShown } from '../../../reduxStore/navigation/actions';
import { RootState } from '../../../store';
import Checkout from './checkout';
import { Disclaimer } from './disclaimer';
import DisclamerNoTrial from '../disclamerNoTrial';
import classNames from 'classnames';
import locales from '../../../content.json';
import s from './index.module.scss';
import { PaymentModalDiscount } from './modalDiscount';
import { setTariff, setPaymentFreeBonus } from '../../../reduxStore/data/actions';
import { getParameterFromURL } from '../../../utils/scripts';
import { getCurrencySymbol } from '../../../utils/scripts/currency';
import { getDiscountTariff } from '../../../utils/scripts/parseTariff';
import { analyticsEventsTracker, GTMOnboardingFunnelEvent, EVENT_TRACKERS } from '../../../utils/analytics';
import { trackBlockScrollDepth } from '../../../utils/scripts/scrollTracker';
import { PAYMENT_TARIFF } from '../../../types';

interface Props {
  label?: any;
  discount?: any;
}

const PaymentModalV5: FC<Props> = props => {
  const { label, discount } = props;
  const dispatch = useDispatch();
  const isPaymentModalShow = useSelector((state: RootState) => state.navigation.paymentModal.show);
  const noPaypal = useSelector((state: RootState) => state.data.noPaypal);
  const product = useSelector((state: RootState) => state.data.paymentModal.product);
  const tariff = useSelector((state: RootState) => state.data.tariff);

  const discountShownOnce = useSelector((s: RootState) => s.navigation.discountShownOnce);
  const [showModalDiscount, setShowModalDiscount] = useState(false);
  const [indexModal, setIndexModal] = useState(false);
  const [indexOffer, setIndexOffer] = useState(false);
  const subscrUK = getParameterFromURL('subscrUK');
  const version = subscrUK ? 'version2UK_with_offer' : 'version2_with_offer';
  const element = document.getElementById('scrollContent') || undefined;
  const firstScreenAB = getParameterFromURL('fSW') === 'true';
  const tariffURL = getParameterFromURL('tariff');
  const isV43orV44 = tariff === 'v43' || tariff === 'v44';
  const hasTrial = Boolean(product?.details?.trial_amount);
  const shouldShowNoTrialDisclaimer = isV43orV44 && !hasTrial;
  const isSinglePlanCheckout = Boolean(getParameterFromURL('singlePlanCheckout'));
  const hideClose = tariff === PAYMENT_TARIFF.WINBACK_V1 || isSinglePlanCheckout;

  const p20RemoveSpecialOffe = getParameterFromURL('p20RemoveSpecialOffe') === 'true';
  const removeSpecialOffer = p20RemoveSpecialOffe || tariff === 'v49';

  useEffect(() => {
    trackBlockScrollDepth(s.paymentModal, 'checkout');
    setIndexModal(true);
  }, []);

  useEffect(() => {
    if (element && isPaymentModalShow) {
      setTimeout(() => {
        element.parentElement?.scrollTo({
          top: element.offsetTop,
          behavior: 'auto',
        });
      }, 10);
    }
  }, [isPaymentModalShow, element]);

  const handleClose = () => {

    if (indexModal && !discountShownOnce && !removeSpecialOffer) {
      showModal();
    } else {
      if (indexOffer) {
        addEventsShowOfferPaywall();
        setIndexOffer(false);
      }
      dispatch(hidePaymentModal());
      setShowModalDiscount(false);
      if (label) {
        label.scrollIntoView();
      }
    }
  };

  function showModal() {
    setShowModalDiscount(true);
    addEventsShowSpecialOffer();
    setIndexModal(false);
    dispatch(showSpecialOffer());
    dispatch(setTariff(getDiscountTariff(tariff)));
    dispatch(markDiscountShown());
    setIndexOffer(true);
  }

  const closeHandler = () => {
    setShowModalDiscount(false);
    dispatch(hidePaymentModal());
    addEventsShowOfferPaywall();
    setIndexOffer(false);
    if (discount) {
      discount.scrollIntoView();
    }
  };

  function addEventsShowSpecialOffer() {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showSpecialOffer(), EVENT_TRACKERS.GTM);
  }

  function addEventsShowOfferPaywall() {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showOfferPaywall(version), EVENT_TRACKERS.GTM);
  }

  function getPeriod(product) {
    const duration = product.details.duration;

    if (duration) {
      let resultPeriodTextValue = `${duration.value} ${duration.unit}`;
      const resultTrialPeriodTextValue =
        tariff === 'v31' || tariff === 'v33'
          ? `${duration.trial_period_value} ${duration.trial_period_unit}`
          : `${duration.trial_period_value} ${duration.trial_period_unit}`;

      if (resultTrialPeriodTextValue === '1 week' || resultTrialPeriodTextValue === '7 day') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_117}`;
      }

      if (resultTrialPeriodTextValue === '1 month') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_66}`;
      }

      if (resultTrialPeriodTextValue === '3 month') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_67}`;
      }

      if (resultTrialPeriodTextValue === '6 month') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_227}`;
      }

      if (resultPeriodTextValue === '1 month') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_66}`;
      }

      if (resultPeriodTextValue === '3 month') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_67}`;
      }

      if (resultPeriodTextValue === '6 month') {
        resultPeriodTextValue = `${locales[window.userLang].payment_text_227}`;
      }

      let resultPeriodText;

      if (resultPeriodTextValue) {
        resultPeriodText = resultPeriodTextValue
          .toLowerCase()
          .replace(/-\w/g, match => match.toUpperCase())
          .replace(/^\w/, match => match.toUpperCase());
      }
      return resultPeriodText;
    }
  }

  function getPrice(product) {
    if (product) {
      const resultPriceTextValue = product?.details?.trial_amount || product?.details?.amount;
      // v002/v003 are region-specific products — always shown in their own currency
      const forcedCurrency =
        tariff === PAYMENT_TARIFF.V002 ? '€' : tariff === PAYMENT_TARIFF.V003 ? '£' : null;
      const currencySymbol = forcedCurrency ?? getCurrencySymbol();

      return `${currencySymbol}${resultPriceTextValue}`;
    }
  }

  useEffect(() => {
    if (document.querySelector('#paymentModal_subtitleText4') && product) {
      document.querySelector('#paymentModal_subtitleText4').innerHTML = getPeriod(product) + ' ';
    }
    if (document.querySelector('#paymentModal_subtitleText2') && product) {
      document.querySelector('#paymentModal_subtitleText2').innerHTML = getPrice(product) + ' ';
    }
  }, [product, isPaymentModalShow]);

  return (
    <section className={classNames(s.paymentModal, isPaymentModalShow ? s.paymentModal_shown : s.paymentModal_hide)}>
      <PaymentModalDiscount closeHandler={closeHandler} showModalDiscount={showModalDiscount} />

      {!showModalDiscount && (
        <div className={s.paymentModal_Wrapper} id="scrollContent">
          <div className={s.paymentModal_content}>
            {!hideClose && <div className={s.paymentModal_close} onClick={handleClose} aria-label="Close" />}
            <h2
              className={s.paymentModal_title}
              dangerouslySetInnerHTML={{ __html: locales[window.userLang].checkoutV2_text9 }}
            />
            <p className={s.paymentModal_subtitle}>
              <span className={s.paymentModal_subtitleText1}>{locales[window.userLang].checkoutV2_text1}: </span>
              <span className={s.paymentModal_subtitleText2} id="paymentModal_subtitleText2"></span>
              <span className={s.paymentModal_subtitleText3}>{locales[window.userLang].for} </span>
              <span className={s.paymentModal_subtitleText4} id="paymentModal_subtitleText4"></span>
            </p>
            <div className={s.paymentModal_contentTextBlock}>
              <p className={s.paymentModal_contentText1}>{locales[window.userLang].checkoutV2_text10}</p>
              <p className={s.paymentModal_contentText2}>
                <span />
                {locales[window.userLang].checkoutV2_text11}
              </p>
            </div>
            <Checkout product={product} />
            <div className={s.paymentModal_guarantee}>
              <span />
              <p>{locales[window.userLang].checkoutV2_text8}</p>
            </div>
            {shouldShowNoTrialDisclaimer ? (
              <DisclamerNoTrial
                product={product}
                price={product?.details?.amount}
                period={`${product?.details?.duration?.value} ${product?.details?.duration?.unit}`}
              />
            ) : (
              <Disclaimer />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default PaymentModalV5;
